import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { db, generateId } from '@/lib/db';

const SongContext = createContext(null);

export function SongProvider({ children }) {
    const [songs, setSongs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentSong, setCurrentSong] = useState(null);

    // Load all songs from IndexedDB
    const loadSongs = useCallback(async () => {
        try {
            const allSongs = await db.getAllSongs();
            setSongs(allSongs);
        } catch (error) {
            console.error('Error loading songs:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSongs();
    }, [loadSongs]);

    // Create a new song
    const createSong = useCallback(async () => {
        const newSong = {
            id: generateId(),
            title: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            fragments: []
        };
        
        await db.saveSong(newSong);
        await loadSongs();
        return newSong;
    }, [loadSongs]);

    // Load a specific song by ID
    const loadSong = useCallback(async (songId) => {
        try {
            const song = await db.getSong(songId);
            setCurrentSong(song);
            return song;
        } catch (error) {
            console.error('Error loading song:', error);
            return null;
        }
    }, []);

    // Update song title
    const updateSongTitle = useCallback(async (songId, title) => {
        const song = await db.getSong(songId);
        if (song) {
            song.title = title;
            song.updatedAt = new Date().toISOString();
            await db.saveSong(song);
            setCurrentSong(song);
            await loadSongs();
        }
    }, [loadSongs]);

    // Add a fragment to a song
    const addFragment = useCallback(async (songId, fragment) => {
        if (!songId) {
            console.error('addFragment: songId is required');
            throw new Error('Song ID is required');
        }
        
        const song = await db.getSong(songId);
        if (!song) {
            console.error('addFragment: Song not found with ID:', songId);
            throw new Error('Song not found');
        }
        
        const newFragment = {
            id: generateId(),
            createdAt: new Date().toISOString(),
            ...fragment
        };
        song.fragments.push(newFragment);
        song.updatedAt = new Date().toISOString();
        await db.saveSong(song);
        setCurrentSong({ ...song });
        await loadSongs();
        return newFragment;
    }, [loadSongs]);

    // Update a fragment
    const updateFragment = useCallback(async (songId, fragmentId, updates) => {
        const song = await db.getSong(songId);
        if (song) {
            const fragmentIndex = song.fragments.findIndex(f => f.id === fragmentId);
            if (fragmentIndex !== -1) {
                song.fragments[fragmentIndex] = {
                    ...song.fragments[fragmentIndex],
                    ...updates
                };
                song.updatedAt = new Date().toISOString();
                await db.saveSong(song);
                setCurrentSong({ ...song });
                await loadSongs();
            }
        }
    }, [loadSongs]);

    // Delete a fragment
    const deleteFragment = useCallback(async (songId, fragmentId) => {
        const song = await db.getSong(songId);
        if (song) {
            // If fragment has audio blob, delete it
            const fragment = song.fragments.find(f => f.id === fragmentId);
            if (fragment?.type === 'audio' && fragment.blobId) {
                await db.deleteAudioBlob(fragment.blobId);
            }
            
            song.fragments = song.fragments.filter(f => f.id !== fragmentId);
            song.updatedAt = new Date().toISOString();
            await db.saveSong(song);
            setCurrentSong({ ...song });
            await loadSongs();
        }
    }, [loadSongs]);

    // Reorder fragments (drag and drop)
    const reorderFragments = useCallback(async (songId, fromIndex, toIndex) => {
        const song = await db.getSong(songId);
        if (song && fromIndex !== toIndex) {
            const fragments = [...song.fragments];
            const [movedFragment] = fragments.splice(fromIndex, 1);
            fragments.splice(toIndex, 0, movedFragment);
            
            song.fragments = fragments;
            song.updatedAt = new Date().toISOString();
            await db.saveSong(song);
            setCurrentSong({ ...song });
            await loadSongs();
        }
    }, [loadSongs]);

    // Delete a song
    const deleteSong = useCallback(async (songId) => {
        const song = await db.getSong(songId);
        if (song) {
            // Delete all audio blobs associated with the song
            for (const fragment of song.fragments) {
                if (fragment.type === 'audio' && fragment.blobId) {
                    await db.deleteAudioBlob(fragment.blobId);
                }
            }
            await db.deleteSong(songId);
            setCurrentSong(null);
            await loadSongs();
        }
    }, [loadSongs]);

    // Move fragment to another song
    const moveFragment = useCallback(async (fromSongId, fragmentId, toSongId) => {
        const fromSong = await db.getSong(fromSongId);
        const toSong = await db.getSong(toSongId);
        
        if (fromSong && toSong) {
            const fragmentIndex = fromSong.fragments.findIndex(f => f.id === fragmentId);
            if (fragmentIndex !== -1) {
                const [fragment] = fromSong.fragments.splice(fragmentIndex, 1);
                // Preserve original timestamp, add moved metadata
                fragment.movedAt = new Date().toISOString();
                fragment.movedFrom = fromSongId;
                toSong.fragments.push(fragment);
                
                fromSong.updatedAt = new Date().toISOString();
                toSong.updatedAt = new Date().toISOString();
                
                await db.saveSong(fromSong);
                await db.saveSong(toSong);
                setCurrentSong({ ...fromSong });
                await loadSongs();
                return true;
            }
        }
        return false;
    }, [loadSongs]);

    // Copy fragment to another song
    const copyFragment = useCallback(async (fromSongId, fragmentId, toSongId) => {
        const fromSong = await db.getSong(fromSongId);
        const toSong = await db.getSong(toSongId);
        
        if (fromSong && toSong) {
            const fragment = fromSong.fragments.find(f => f.id === fragmentId);
            if (fragment) {
                // Create a copy with new ID but preserve original data
                const copiedFragment = {
                    ...fragment,
                    id: generateId(),
                    copiedAt: new Date().toISOString(),
                    copiedFrom: { songId: fromSongId, fragmentId: fragmentId }
                };
                
                // For audio fragments, we reference the same blob (no duplication)
                // This is safe since blobs are immutable
                
                toSong.fragments.push(copiedFragment);
                toSong.updatedAt = new Date().toISOString();
                
                await db.saveSong(toSong);
                await loadSongs();
                return true;
            }
        }
        return false;
    }, [loadSongs]);

    const value = {
        songs,
        loading,
        currentSong,
        createSong,
        loadSong,
        updateSongTitle,
        addFragment,
        updateFragment,
        deleteFragment,
        reorderFragments,
        deleteSong,
        moveFragment,
        copyFragment
    };

    return (
        <SongContext.Provider value={value}>
            {children}
        </SongContext.Provider>
    );
}

export function useSongs() {
    const context = useContext(SongContext);
    if (!context) {
        throw new Error('useSongs must be used within a SongProvider');
    }
    return context;
}